import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowRightLeft, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  listarLeadsDoUsuario,
  transferirLead,
  excluirLead,
} from "@/services/gestaoUsuariosService";
import type { LeadForGestaoUsuario, ProfileForGestao } from "@/types/users";
import type { UserRole } from "@/types/permissions";

const ROLE_LABELS: Partial<Record<UserRole, string>> = {
  admin: "Administrador",
  representante: "Representante",
  vendedor: "Vendedor",
  parceiro: "Parceiro",
  financeiro: "Financeiro",
  ai_agent: "Agente IA",
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return iso;
  }
}

function localLabel(cidade?: string | null, estado?: string | null): string {
  if (cidade && estado) return `${cidade}/${estado}`;
  return cidade || estado || "—";
}

interface UsuarioLeadsGestaoProps {
  userId: string;
  userRole: UserRole;
  usuarios: ProfileForGestao[];
}

export function UsuarioLeadsGestao({ userId, userRole, usuarios }: UsuarioLeadsGestaoProps) {
  const [leads, setLeads] = useState<LeadForGestaoUsuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [transferLead, setTransferLead] = useState<LeadForGestaoUsuario | null>(null);
  const [destinoId, setDestinoId] = useState("");
  const [deleteLead, setDeleteLead] = useState<LeadForGestaoUsuario | null>(null);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarLeadsDoUsuario(userId);
      setLeads(data.leads);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao carregar leads");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const destinos = usuarios.filter(
    (u) => u.ativo && u.id !== userId && (u.role === "parceiro" || u.role === "vendedor")
  );

  const openTransfer = (lead: LeadForGestaoUsuario) => {
    setTransferLead(lead);
    setDestinoId("");
  };

  const closeTransfer = () => {
    setTransferLead(null);
    setDestinoId("");
  };

  const handleTransfer = async () => {
    if (!transferLead || !destinoId) {
      toast.error("Selecione o usuário de destino");
      return;
    }
    setActionLoading(true);
    try {
      const result = await transferirLead(transferLead.id, destinoId);
      toast.success(result.message);
      closeTransfer();
      await loadLeads();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao transferir lead");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteLead) return;
    setActionLoading(true);
    try {
      await excluirLead(deleteLead.id);
      toast.success(`Lead "${deleteLead.nome}" excluído com sucesso`);
      setDeleteLead(null);
      await loadLeads();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao excluir lead");
    } finally {
      setActionLoading(false);
    }
  };

  const sectionTitle =
    userRole === "parceiro"
      ? "Leads cadastrados pelo parceiro"
      : userRole === "vendedor"
        ? "Leads do vendedor"
        : "Leads criados pelo usuário";

  return (
    <div className="space-y-3 border-t pt-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">{sectionTitle}</p>
          <p className="text-xs text-muted-foreground">
            {loading ? "Carregando…" : `${leads.length} lead(s)`}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : leads.length === 0 ? (
        <p className="text-sm text-muted-foreground py-3 text-center">
          Nenhum lead encontrado para este usuário.
        </p>
      ) : (
        <ScrollArea className="h-[220px] rounded-md border">
          <div className="divide-y">
            {leads.map((lead) => (
              <div
                key={lead.id}
                className="flex flex-wrap items-start justify-between gap-2 p-3"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="font-medium text-sm truncate">{lead.nome}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {localLabel(lead.cidade, lead.estado)}
                    {lead.produto ? ` • ${lead.produto}` : ""}
                    {lead.created_at ? ` • ${formatDate(lead.created_at)}` : ""}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {lead.etapa && (
                      <Badge variant="outline" className="text-[10px] font-normal">
                        {lead.etapa}
                      </Badge>
                    )}
                    {lead.status && (
                      <Badge variant="secondary" className="text-[10px] font-normal">
                        {lead.status}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openTransfer(lead)}
                    title="Transferir lead"
                    aria-label={`Transferir lead ${lead.nome}`}
                  >
                    <ArrowRightLeft className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteLead(lead)}
                    title="Excluir lead"
                    aria-label={`Excluir lead ${lead.nome}`}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      )}

      <Dialog open={!!transferLead} onOpenChange={(open) => !open && closeTransfer()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Transferir lead</DialogTitle>
            <DialogDescription>
              Transferir <strong>{transferLead?.nome}</strong> para outro parceiro ou vendedor.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Usuário de destino</Label>
            {destinos.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Não há parceiros ou vendedores ativos disponíveis para transferência.
              </p>
            ) : (
              <Select value={destinoId} onValueChange={setDestinoId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o usuário" />
                </SelectTrigger>
                <SelectContent>
                  {destinos.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.nome} ({ROLE_LABELS[u.role] ?? u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeTransfer}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleTransfer}
              disabled={actionLoading || !destinoId}
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {actionLoading ? " Transferindo…" : "Transferir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteLead} onOpenChange={(open) => !open && setDeleteLead(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir lead</AlertDialogTitle>
            <AlertDialogDescription>
              O lead <strong>{deleteLead?.nome}</strong> e todos os dados relacionados
              (tarefas, faturas, propostas etc.) serão removidos permanentemente. Esta ação
              não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={actionLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {actionLoading ? " Excluindo…" : "Excluir permanentemente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
